"use client";
import Button from "@/components/Button/Button.component";
import { BUTTON_SUBTYPE_CLASS } from "@/components/Button/Button.model";
import Image from "@/components/Image/Image.component";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { CSSTransition } from "react-transition-group";
import TextOverlay from "../TextOverlay/TextOverlay.component";
import { Typography } from "../Typography/Typography.component";
import { CardPropsModel } from "./Card.model";

const Card = ({
  className,
  title,
  brand,
  name,
  thumbnail,
  isHovered,
  slug,
  overlayText,
  changeCardHoverStatus,
}: CardPropsModel) => {
  const [showButton, setShowButton] = useState<boolean>(false);
  const [showActiveOverlay, setShowActiveOverlay] = useState<boolean>(false);
  const cssClass = isHovered ? "md:opacity-50" : "opacity-100";
  const router = useRouter();
  const showOverlay = overlayText ? true : false;

  // only render when we have a valid URL
  const hasThumbnail = Boolean(thumbnail?.url);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className={cn(
        cssClass,
        "relative cursor-default transition-opacity duration-500 ease-in-out outline-none hover:opacity-100 focus:opacity-100",
        className,
      )}
      role="button"
      tabIndex={0}
      onFocus={() => {
        setShowButton(true);
        setShowActiveOverlay(true);
        changeCardHoverStatus?.(true);
      }}
      onBlur={() => {
        setShowButton(false);
        setShowActiveOverlay(false);
        changeCardHoverStatus?.(false);
      }}
      onClick={() => {
        setShowButton(false);
        changeCardHoverStatus?.(false);
      }}
      onKeyDown={event => {
        if (event.key === "Enter") {
          router.push(slug);
        }
      }}
    >
      <div
        className="relative h-full overflow-hidden outline-none"
        onMouseEnter={() => {
          setShowButton(true);
          setShowActiveOverlay(true);
          changeCardHoverStatus?.(true);
        }}
        onMouseLeave={() => {
          setShowButton(false);
          setShowActiveOverlay(false);
          changeCardHoverStatus?.(false);
        }}
      >
        <Link href={slug} tabIndex={-1} scroll={!showOverlay}>
          {hasThumbnail && (
            <Image
              src={`${thumbnail?.url}?fm=webp`}
              alt={`${name}-${brand}`}
              width={thumbnail?.width ?? 0}
              height={thumbnail?.height ?? 0}
              className={cn(
                showButton && !showOverlay && "scale-105 lg:blur-[20px]",
                "h-auto max-h-[640px] w-full object-cover transition-all duration-500 ease-in-out",
              )}
              hasPlaceholder
            />
          )}

          <div
            className={cn(
              showButton &&
                !showOverlay &&
                "bg-core-black absolute top-0 hidden h-full w-full opacity-10 md:block",
            )}
          ></div>
        </Link>
        {!showOverlay && (
          <div className="absolute top-1/2 left-1/2 z-10 h-6 -translate-x-1/2 -translate-y-1/2 transform outline-none max-lg:hidden">
            <Button
              primary={false}
              type={BUTTON_SUBTYPE_CLASS.SECONDARY_WHITE}
              content={{ text: "VIEW", url: slug }}
              className={`opacity-0 ${
                showButton
                  ? "animate-slide-up-150 opacity-100"
                  : "animate-slide-down-150 opacity-0 transition-opacity duration-500"
              }`}
            />
          </div>
        )}

        <CSSTransition
          in={showOverlay && showActiveOverlay}
          timeout={1000}
          unmountOnExit
          classNames={{
            enter: "transition-translate duration-500 animate-slide-up",
            exit: "transition-translate duration-500 animate-slide-down",
            exitDone: "hidden",
          }}
        >
          <TextOverlay text={overlayText || ""} />
        </CSSTransition>

        {showOverlay && (
          <Image
            width={32}
            height={32}
            src={
              showActiveOverlay
                ? "/static/icons/close-circle.png"
                : "/static/icons/open-circle.png"
            }
            alt={showActiveOverlay ? "close-circle" : "open-circle"}
            className={cn(
              showActiveOverlay
                ? "animate-rotate-90"
                : "animate-rotate-90-negative",
              "bottom-xxs right-xxs pointer-events-auto absolute z-13 cursor-pointer transition-transform duration-500 lg:pointer-events-none",
            )}
            onClick={() => {
              setShowActiveOverlay(!showActiveOverlay);
            }}
          />
        )}
      </div>

      <div className="text-sl flex justify-between pt-4">
        <Link
          href={slug}
          tabIndex={-1}
          scroll={!showOverlay}
          className="no-underline"
        >
          <div
            className="flex flex-col gap-y-1"
            onMouseEnter={() => {
              setShowButton(true);
              setShowActiveOverlay(true);
              changeCardHoverStatus?.(true);
            }}
            onMouseLeave={() => {
              setShowButton(false);
              setShowActiveOverlay(false);
              changeCardHoverStatus?.(false);
            }}
          >
            {title && (
              <div className="absolute top-0 left-0 z-10 h-full w-full p-4">
                <Typography.H6
                  additionalClassName={cn(
                    "normal-case",
                    isHovered ? "opacity-50" : "opacity-100",
                  )}
                >
                  {title}
                </Typography.H6>
              </div>
            )}
            {brand && (
              <Typography.LABEL1
                additionalClassName={cn(
                  "text-core-black",
                  isHovered ? "opacity-50" : "opacity-100",
                )}
              >
                {brand}
              </Typography.LABEL1>
            )}
            {name && (
              <Typography.LABEL2
                additionalClassName={cn(
                  "text-core-black",
                  isHovered ? "opacity-50" : "opacity-100",
                )}
              >
                {name}
              </Typography.LABEL2>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Card;
